import { optionalFeeImageUrl } from '@/lib/optionalFeeIcons'
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Play, Plus, Save, Send } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Permit } from '@/components/guards/Permit'
import {
  FORM_CONTROL_BASE_CLASS,
  FORM_CONTROL_FOCUS_CLASS as fieldFocusClass,
  FORM_CONTROL_SELECT_OPEN_CLASS as selectOpenStateClass,
} from '@/components/ui/form-control-styles'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { EntityPicker } from '@/components/shared/EntityPicker'
import { ClientNameWithProspect } from '@/components/shared/StatusBadges'
import {
  OptionalChargesSection,
  PendingAttachFiles,
  type OptionalCharge,
  type PendingAttachFilesHandle,
} from '@/components/shared'
import { LineItemsEditor } from '@/components/billing/LineItemsEditor'
import { InvoiceSaveAndSendDialog } from '@/components/billing/InvoiceSaveAndSendDialog'
import { QuoteGrandTotalSummary } from '@/components/billing/QuoteGrandTotalSummary'
import { TradeDiscountEditor } from '@/components/billing/TradeDiscountEditor'
import {
  ServiceItemLibraryPage,
  type SelectedServiceItem,
} from '@/components/billing/ServiceItemLibraryPage'
import { AddQuoteServiceDrawer } from '@/components/billing/QuoteManualEntryDrawers'
import { OptionalFeeDocumentDialog } from '@/components/settings/services/OptionalFeeDocumentDialog'
import {
  useInvoice,
  useCreateInvoice,
  useUpdateInvoice,
} from '@/hooks/useInvoices'
import { useQuoteOptionalFeeTemplates } from '@/hooks/useQuotes'
import { useActiveServiceCategories } from '@/hooks/useServiceCategoryOptions'
import { invoicesApi } from '@/services/invoices'
import { clientsApi } from '@/services/clients'
import { contractsApi } from '@/services/contracts'
import { jobsApi } from '@/services/jobs'
import { getApiErrorMessage } from '@/services/api'
import { toast } from '@/components/ui/toast'
import { computeDocumentTotals } from '@/lib/documentTotals'
import { cn } from '@/lib/utils'
import { formatCurrency, formatDate } from '@/lib/format'
import {
  computeLineTotal,
  emptyLineItem,
  missingUnitPriceIndexes,
} from '@/lib/lineItems'
import type {
  Client,
  Contract,
  Job,
  LineItem,
  CreateInvoiceRequest,
  ServiceItem,
  Surcharge,
} from '@/types'
import { useJob } from '@/hooks/useJobs'

interface Props {
  mode: 'create' | 'edit'
}

type DiscountType = 'percent' | 'fixed'
type SendAfterSaveMode = 'final' | 'draft'

const PAYMENT_DEADLINE_OPTIONS = [15, 30, 45, 60] as const

const outlineBtnClass =
  'h-10 gap-2 rounded-xl border border-[#e7e9ef] bg-white p-3 text-sm font-medium normal-case leading-none text-[#171a20] hover:bg-[#fbfbfc] hover:text-[#171a20]'

const primaryHeaderBtnClass =
  'h-10 gap-2 rounded-xl border border-[#ff5a1f] bg-[#ff5a1f] p-3 text-sm font-medium normal-case leading-none text-white hover:bg-[#e6511c]'

const clientInfoCardClass =
  'flex min-w-[240px] flex-col overflow-hidden rounded-2xl border border-[#e7e9ef] bg-white shadow-[0_1px_4px_-4px_rgba(38,45,66,0.05),0_8px_16px_-4px_rgba(38,45,66,0.1)]'

const invoiceLabelClass =
  'flex h-4 w-full shrink-0 items-center gap-1 overflow-hidden whitespace-nowrap text-xs font-medium normal-case leading-4 text-[#171a20]'

const invoiceFieldBaseClass = cn(
  'rounded-xl border px-4 py-3 normal-case leading-[1.4] shadow-none placeholder:font-normal placeholder:text-[#6c7285]',
  FORM_CONTROL_BASE_CLASS
)

const invoiceFieldClass = cn('h-10', invoiceFieldBaseClass, fieldFocusClass)

const invoicePickerClass = cn(
  'h-10 hover:bg-[#eef0f5] [&>span]:font-normal [&>span>span]:font-normal',
  invoiceFieldBaseClass,
  selectOpenStateClass
)

const invoiceSelectClass = cn(
  'h-10 data-[placeholder]:font-normal [&>span]:font-normal [&>span>span]:font-normal',
  invoiceFieldBaseClass,
  selectOpenStateClass
)

const newClientBtnClass =
  'h-10 shrink-0 gap-2 rounded-xl border border-dashed border-[#ff8b66] bg-white px-4 text-sm font-medium normal-case leading-none text-[#ff5a1f] shadow-none hover:bg-[#fff1ea] hover:text-[#ff5a1f]'

const invoiceTextareaClass = cn(
  'min-h-[138px] resize-y',
  invoiceFieldBaseClass,
  fieldFocusClass
)

function InvoiceStepCard({
  step,
  title,
  children,
  className,
  contentClassName,
  headerBadge,
}: {
  step: number
  title: string
  children: ReactNode
  className?: string
  contentClassName?: string
  headerBadge?: ReactNode
}) {
  return (
    <section className={cn(clientInfoCardClass, className)}>
      <div className='flex h-14 w-full items-center overflow-hidden px-6 pb-2 pt-4'>
        <div className='flex min-w-0 flex-1 items-center gap-3 overflow-hidden'>
          <span className='flex size-6 shrink-0 items-center justify-center rounded-md bg-[#ff5a1f] font-sans text-xs font-medium leading-6 text-white'>
            {step}
          </span>
          <h2 className='truncate font-heading text-sm font-semibold normal-case leading-[1.2] text-[#171a20]'>
            {title}
          </h2>
          {headerBadge ? (
            <span className='inline-flex h-6 shrink-0 items-center justify-center rounded-lg bg-[#fff1ea] px-2 py-1 font-sans text-xs font-medium leading-4 text-[#ff5a1f]'>
              {headerBadge}
            </span>
          ) : null}
        </div>
      </div>
      <div
        className={cn(
          'flex w-full flex-1 flex-col gap-4 px-6 pb-6 pt-2',
          contentClassName
        )}
      >
        {children}
      </div>
    </section>
  )
}

function RequiredInvoiceLabel({
  htmlFor,
  children,
}: {
  htmlFor?: string
  children: ReactNode
}) {
  return (
    <Label htmlFor={htmlFor} className={invoiceLabelClass}>
      {children}
      <span className='text-[#d63b33]'>*</span>
    </Label>
  )
}

function deadlineFromDates(
  issueDate: string | null | undefined,
  dueDate: string | null | undefined
): number | '' {
  if (!issueDate || !dueDate) return ''

  const issue = new Date(`${issueDate}T00:00:00`)
  const due = new Date(`${dueDate}T00:00:00`)
  const diffDays = Math.round((due.getTime() - issue.getTime()) / 86_400_000)

  return PAYMENT_DEADLINE_OPTIONS.find(days => days === diffDays) ?? ''
}

function jobLabel(job: Pick<Job, 'id' | 'reference' | 'client_name'>): string {
  const reference = job.reference ?? job.id.slice(0, 8)
  return job.client_name ? `${reference} - ${job.client_name}` : reference
}

function contractLabel(
  contract: Pick<Contract, 'id' | 'number' | 'title'>
): string {
  const reference = contract.number ?? contract.id.slice(0, 8)
  return contract.title ? `${reference} - ${contract.title}` : reference
}

function lineFromServiceItem(item: ServiceItem): LineItem {
  const base = {
    service_item_id: item.id,
    description: item.name,
    service_description: item.description,
    image_file_id: item.image_file_id ?? null,
    quantity: 1,
    unit_price: item.unit_price,
    tax_rate: item.tax_rate ?? 20,
    image_url: item.image_url ?? null,
  }

  return {
    ...base,
    line_total: computeLineTotal(base),
  }
}

// 2.6.3 Create invoice manually / edit - line items, optional charges, amount
// summary, attachments and totals (feedback #40-#44, mirrors the quote form).
export default function InvoiceFormPage({ mode }: Props) {
  const { t } = useTranslation('invoices')
  const { t: tQuotes } = useTranslation('quotes')
  useActiveServiceCategories()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const preselectClient = searchParams.get('client_id') ?? undefined
  const preselectJob = searchParams.get('job_id') ?? undefined

  const { data: detail, isLoading } = useInvoice(
    mode === 'edit' ? id : undefined
  )
  const { data: optionalFeeTemplates } = useQuoteOptionalFeeTemplates()
  const create = useCreateInvoice()
  const update = useUpdateInvoice(id ?? '')
  // Create-mode attachments: collected locally, flushed after the invoice exists.
  const pendingAttachments = useRef<PendingAttachFilesHandle>(null)

  const [clientId, setClientId] = useState(preselectClient ?? '')
  const [jobId, setJobId] = useState(
    mode === 'create' ? (preselectJob ?? '') : ''
  )
  const [titleValue, setTitleValue] = useState('')
  const [contractId, setContractId] = useState('')
  const [purchaseOrder, setPurchaseOrder] = useState('')
  const [paymentDeadlineDays, setPaymentDeadlineDays] = useState<number | ''>(
    30
  )
  const [linkedJob, setLinkedJob] = useState<Job | null>(null)
  const [prefilledJobId, setPrefilledJobId] = useState('')
  const [items, setItems] = useState<LineItem[]>([])
  const [surcharges, setSurcharges] = useState<OptionalCharge[]>([])
  const [discountType, setDiscountType] = useState<DiscountType>('percent')
  const [discountValue, setDiscountValue] = useState(0)
  const [tradeDiscountOpen, setTradeDiscountOpen] = useState(false)
  const [libraryPageOpen, setLibraryPageOpen] = useState(false)
  const [serviceDrawerOpen, setServiceDrawerOpen] = useState(false)
  const [optionalFeeDrawerOpen, setOptionalFeeDrawerOpen] = useState(false)
  const [editingOptionalFeeIndex, setEditingOptionalFeeIndex] = useState<
    number | null
  >(null)
  const [notes, setNotes] = useState('')
  const [allowOnlinePayment, setAllowOnlinePayment] = useState(false)
  const [errors, setErrors] = useState<{
    client_id?: string
    items?: string
    itemFields?: Record<number, { unit_price?: boolean }>
    surcharges?: string
  }>({})
  const [saving, setSaving] = useState(false)
  const [saveAndSendOpen, setSaveAndSendOpen] = useState(false)
  const tradeDiscountEditorRef = useRef<HTMLDivElement | null>(null)

  // Name of the currently selected client so the picker shows it right away
  // when the preloaded first page does not contain it. Mirrors the quote form.
  const { data: selectedClient } = useQuery({
    queryKey: ['clients', 'detail', clientId],
    queryFn: () => clientsApi.get(clientId),
    enabled: !!clientId,
  })
  const { data: selectedJobData } = useJob(jobId || undefined)
  const selectedJob = linkedJob ?? selectedJobData?.data ?? null

  const lineItemFromJob = useCallback(
    (job: Job): LineItem => {
      const amount = job.amount_to_collect ?? undefined
      return {
        ...emptyLineItem(),
        description: t('form.jobLineDescription', {
          reference: job.reference ?? job.id.slice(0, 8),
        }),
        quantity: 1,
        unit_price: amount,
        tax_rate: 0,
        line_total: amount ?? 0,
      }
    },
    [t]
  )

  useEffect(() => {
    if (mode === 'edit' && detail?.data) {
      const inv = detail.data
      // Hydrating local form fields from the fetched invoice (one-shot external sync).
      /* eslint-disable react-hooks/set-state-in-effect */
      setClientId(inv.client_id)
      setJobId(inv.job_id ?? '')
      setTitleValue(inv.title ?? '')
      setContractId(inv.contract_id ?? '')
      setPurchaseOrder(inv.purchase_order ?? '')
      setPaymentDeadlineDays(
        inv.payment_deadline_days ??
          deadlineFromDates(inv.issue_date, inv.due_date) ??
          30
      )
      setItems(inv.items?.length ? inv.items : [])
      setSurcharges(
        (inv.surcharges ?? []).map(s => ({
          id: s.id,
          type: s.type,
          description: s.description ?? '',
          image_file_id: s.image_file_id ?? null,
          image_url: s.image_url ?? null,
          amount: s.amount,
          tax_rate: s.tax_rate,
        }))
      )
      setDiscountType(inv.discount_type === 'fixed' ? 'fixed' : 'percent')
      setDiscountValue(inv.discount_value ?? 0)
      setTradeDiscountOpen(!!inv.discount_type && (inv.discount_value ?? 0) > 0)
      setNotes(inv.notes ?? '')
      setAllowOnlinePayment(!!inv.allow_online_payment)
      /* eslint-enable react-hooks/set-state-in-effect */
    }
  }, [mode, detail])

  useEffect(() => {
    if (mode !== 'create' || !selectedJob || selectedJob.id === prefilledJobId)
      return

    /* eslint-disable react-hooks/set-state-in-effect */
    setClientId(selectedJob.client_id)
    setItems([lineItemFromJob(selectedJob)])
    setErrors(e => ({
      ...e,
      client_id: undefined,
      items: undefined,
      itemFields: undefined,
    }))
    setPrefilledJobId(selectedJob.id)
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [mode, selectedJob, prefilledJobId, lineItemFromJob])

  const totals = useMemo(
    () =>
      computeDocumentTotals(
        items,
        surcharges,
        discountValue > 0 ? { type: discountType, value: discountValue } : null
      ),
    [items, surcharges, discountType, discountValue]
  )
  const surchargeTotal = totals.surcharge_total
  const hasTradeDiscountRow = tradeDiscountOpen && discountValue > 0
  const taxBreakdown = useMemo(() => {
    return totals.tax_breakdown
      .filter(row => row.tax > 0)
      .map(row => ({
        label: tQuotes('form.grandTotal.vatRateLabel', { rate: row.rate }),
        amount: row.tax,
      }))
  }, [totals.tax_breakdown, tQuotes])
  const surchargeVatRate = useMemo(() => {
    const rates = Array.from(
      new Set(items.map(item => item.tax_rate).filter(rate => rate > 0))
    ).sort((a, b) => a - b)

    return rates[0] ?? 0
  }, [items])
  const optionalFeeQuickPresets =
    optionalFeeTemplates?.data?.map(template => ({
      id: template.id,
      type: template.type,
      label: template.name,
      amount: template.amount,
      description: template.description,
      image_file_id: template.image_file_id ?? null,
      image_url: optionalFeeImageUrl(template),
    })) ?? []

  const validate = () => {
    const next: typeof errors = {}
    if (!clientId) next.client_id = t('form.validation.clientRequired')
    const incompleteIdx = items.findIndex(it => !it.description?.trim())
    if (items.length === 0) next.items = t('form.validation.itemsRequired')
    else if (incompleteIdx >= 0)
      next.items = t('form.validation.itemIncomplete', {
        line: incompleteIdx + 1,
      })
    const missingUnitPrices = missingUnitPriceIndexes(items)
    if (missingUnitPrices.length > 0) {
      next.itemFields = Object.fromEntries(
        missingUnitPrices.map(index => [index, { unit_price: true }])
      )
      toast({
        variant: 'destructive',
        title: t('form.validation.unitPriceRequired'),
      })
    }
    if (surcharges.some(s => s.type === 'custom' && !s.description?.trim()))
      next.surcharges = t('form.validation.surchargeDescriptionRequired')
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const buildBody = (): CreateInvoiceRequest => ({
    client_id: clientId,
    title: titleValue.trim() || null,
    job_id: mode === 'edit' ? jobId || null : jobId || undefined,
    quote_id: selectedJob?.quote_id ?? undefined,
    contract_id: contractId || undefined,
    payment_deadline_days: paymentDeadlineDays || undefined,
    purchase_order: purchaseOrder.trim() || undefined,
    items,
    surcharges: surcharges.map(
      (s): Surcharge => ({
        type: s.type,
        description: s.description || undefined,
        image_file_id: s.image_file_id ?? undefined,
        amount: s.amount,
        tax_rate: s.tax_rate ?? surchargeVatRate,
      })
    ),
    discount_type: discountValue > 0 ? discountType : null,
    discount_value: discountValue > 0 ? discountValue : 0,
    notes: notes || undefined,
    // Feedback #43: persist the online-payment flag (data-capture only).
    allow_online_payment: allowOnlinePayment,
  })

  const save = async (sendAfterSave?: SendAfterSaveMode) => {
    if (!validate()) return
    setSaving(true)
    try {
      if (mode === 'edit' && id) {
        await update.mutateAsync(buildBody())
        if (pendingAttachments.current?.count()) {
          try {
            await pendingAttachments.current.flush(id)
          } catch {
            toast({ variant: 'destructive', title: t('form.attachError') })
          }
        }
        if (sendAfterSave) {
          navigate(`/invoices/${id}?send=1&send_mode=${sendAfterSave}`)
        } else {
          toast({ variant: 'success', title: t('form.saveSuccess') })
          navigate(`/invoices/${id}`)
        }
      } else {
        const res = await create.mutateAsync(buildBody())
        const newId = res.data?.id
        // Persist any files picked during create now that the invoice exists.
        if (newId && pendingAttachments.current?.count()) {
          try {
            await pendingAttachments.current.flush(newId)
          } catch {
            toast({ variant: 'destructive', title: t('form.attachError') })
          }
        }
        if (sendAfterSave && newId) {
          navigate(`/invoices/${newId}?send=1&send_mode=${sendAfterSave}`)
        } else {
          toast({ variant: 'success', title: t('form.saveSuccess') })
          if (newId) navigate(`/invoices/${newId}`)
        }
      }
    } catch (e) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(e, t('form.saveError')),
      })
    } finally {
      setSaving(false)
    }
  }

  const validateAndSend = () => {
    setSaveAndSendOpen(false)
    void save('final')
  }

  const sendAsDraft = () => {
    setSaveAndSendOpen(false)
    void save('draft')
  }

  const openSaveAndSend = () => {
    if (!validate()) return
    setSaveAndSendOpen(true)
  }

  const appendLineItems = (newLines: LineItem[]) => {
    if (newLines.length === 0) return
    setItems(current => {
      const isPristine =
        current.length === 1 &&
        !current[0].description &&
        !current[0].service_item_id &&
        current[0].unit_price === undefined
      return isPristine ? newLines : [...current, ...newLines]
    })
    setErrors(current => ({
      ...current,
      items: undefined,
      itemFields: undefined,
    }))
  }

  const appendFromLibrary = (picked: SelectedServiceItem[]) => {
    if (picked.length === 0) return

    const newLines = picked.map(({ item, quantity }) => {
      const line = lineFromServiceItem(item)
      return {
        ...line,
        quantity,
        line_total: computeLineTotal({
          quantity,
          unit_price: line.unit_price,
          tax_rate: line.tax_rate,
        }),
      }
    })

    appendLineItems(newLines)
    setLibraryPageOpen(false)
    window.scrollTo({ top: 0 })
  }

  const appendManualService = (item: LineItem) => {
    appendLineItems([item])
  }

  const appendOptionalFee = (charge: OptionalCharge) => {
    setSurcharges(current => [...current, charge])
    setErrors(current => ({ ...current, surcharges: undefined }))
  }

  const updateOptionalFee = (charge: OptionalCharge) => {
    setSurcharges(current =>
      current.map((item, index) =>
        index === editingOptionalFeeIndex ? charge : item
      )
    )
    setErrors(current => ({ ...current, surcharges: undefined }))
  }

  const scrollToTradeDiscountEditor = () => {
    tradeDiscountEditorRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    })
  }

  const preview = async () => {
    if (mode === 'edit' && id) {
      try {
        const res = await invoicesApi.pdf(id)
        if (res.data?.url) window.open(res.data.url, '_blank', 'noopener')
      } catch (e) {
        toast({
          variant: 'destructive',
          title: getApiErrorMessage(e, t('form.previewError')),
        })
      }
    } else {
      toast({ variant: 'default', title: t('form.saveFirst') })
    }
  }

  if (mode === 'edit' && isLoading) {
    return (
      <div className='py-10 text-center text-sm font-normal normal-case text-[#6c7285]'>
        {t('form.loading')}
      </div>
    )
  }

  const finaliseMode = mode === 'edit' && searchParams.get('finalise') === '1'
  const title = finaliseMode
    ? t('form.finaliseTitle')
    : mode === 'edit'
      ? t('form.editTitle')
      : t('form.createTitle')
  const editingOptionalFee =
    editingOptionalFeeIndex === null
      ? null
      : (surcharges[editingOptionalFeeIndex] ?? null)

  return (
    <div className='-mx-6 -mt-6 flex min-h-full flex-col bg-[#f4f5f8]'>
      {/* Page header (Figma 3604:91060). */}
      <header className='sticky top-0 z-20 box-border h-[72px] border-b border-[#e7e9ef] bg-white shadow-[0_1px_4px_0_rgba(38,45,66,0.05)]'>
        <div className='flex h-full flex-wrap items-center gap-6 px-16'>
          <h1 className='sr-only'>{title}</h1>
          <div className='flex min-w-0 flex-1 items-center gap-3'>
            <span className='inline-flex h-8 shrink-0 items-center justify-center rounded-lg bg-[#f5f6fa] px-2 text-sm font-medium normal-case leading-[1.4] text-[#171a20]'>
              {t('list.status.proForma')}
            </span>
          </div>
          <div className='flex flex-wrap items-center gap-3'>
            <Button
              type='button'
              variant='outline'
              className={outlineBtnClass}
              onClick={() => navigate(-1)}
            >
              {t('form.cancel')}
            </Button>
            <Button
              type='button'
              variant='outline'
              className={outlineBtnClass}
              onClick={preview}
            >
              <Play className='size-4' />
              {t('form.previewInvoice')}
            </Button>
            <Button
              type='button'
              variant='outline'
              className={outlineBtnClass}
              disabled={saving}
              onClick={() => void save()}
            >
              <Save className='size-4' />
              {t('form.save')}
            </Button>
            <Button
              type='button'
              className={primaryHeaderBtnClass}
              disabled={saving}
              onClick={openSaveAndSend}
            >
              <Send className='size-4' />
              {t('form.sendToClient')}
            </Button>
          </div>
        </div>
      </header>

      {/* Form body (Figma 1085:12647). */}
      <div className='flex flex-1 flex-col gap-6 bg-[#fafafa] px-16 py-6'>
        {/* Client Information. */}
        <section className={clientInfoCardClass}>
          <div className='flex h-14 w-full items-center overflow-hidden px-6 pb-2 pt-4'>
            <div className='flex min-w-0 flex-1 items-center gap-3 overflow-hidden'>
              <span className='flex size-6 shrink-0 items-center justify-center rounded-md bg-[#ff5a1f] font-sans text-xs font-medium leading-6 text-white'>
                1
              </span>
              <h2 className='truncate font-heading text-sm font-semibold normal-case leading-[1.2] text-[#171a20]'>
                {t('form.clientDetails')}
              </h2>
            </div>
          </div>

          <div className='flex w-full flex-col gap-4 px-6 pb-6 pt-2'>
            <div className='grid gap-4 sm:grid-cols-[1fr_auto] sm:items-end'>
              <div className='flex w-full flex-col items-start gap-1'>
                <RequiredInvoiceLabel>{t('form.client')}</RequiredInvoiceLabel>
                <EntityPicker<Client>
                  queryKey='clients'
                  preload
                  variant='search'
                  placeholder={t('form.searchClients')}
                  fetcher={({ search, limit, page }) =>
                    clientsApi.list({
                      search,
                      limit,
                      page,
                      sort_column: 'name',
                      sort_order: 'asc',
                    })
                  }
                  getOptionLabel={c => c.name}
                  getOptionValue={c => c.id}
                  renderOption={c => (
                    <ClientNameWithProspect
                      name={c.name}
                      category={c.category}
                    />
                  )}
                  renderValue={(label, item) => (
                    <ClientNameWithProspect
                      name={label}
                      category={
                        item?.category ?? selectedClient?.data?.category
                      }
                    />
                  )}
                  initialLabel={selectedClient?.data?.name}
                  value={clientId || null}
                  onChange={v => {
                    setClientId(v)
                    setContractId('')
                    if (jobId && selectedJob?.client_id !== v) {
                      setJobId('')
                      setLinkedJob(null)
                      setPrefilledJobId('')
                      setItems([])
                    }
                    setErrors(e => ({
                      ...e,
                      client_id: undefined,
                      itemFields: undefined,
                    }))
                  }}
                  className={cn(
                    invoicePickerClass,
                    errors.client_id && 'border border-destructive'
                  )}
                />
                {errors.client_id && (
                  <p className='text-xs text-destructive'>{errors.client_id}</p>
                )}
              </div>
              <Permit module='clients' action='edit'>
                <Button
                  type='button'
                  variant='outline'
                  className={newClientBtnClass}
                  onClick={() => navigate('/clients/new')}
                >
                  <Plus className='size-4' />
                  {t('form.createNewClient')}
                </Button>
              </Permit>
            </div>

            <div className='flex w-full flex-col items-start gap-1'>
              <Label htmlFor='invoice-title' className={invoiceLabelClass}>
                {t('form.titleField')}
              </Label>
              <Input
                id='invoice-title'
                value={titleValue}
                placeholder={t('form.titlePlaceholder')}
                maxLength={150}
                className={invoiceFieldClass}
                onChange={e => setTitleValue(e.target.value)}
              />
            </div>

            <div className='grid gap-4 lg:grid-cols-4'>
              <div className='flex w-full flex-col items-start gap-1'>
                <Label className={invoiceLabelClass}>
                  {t('form.linkedIntervention')}
                </Label>
                <EntityPicker<Job>
                  queryKey={`jobs-${clientId || 'all'}`}
                  preload
                  placeholder={t('form.searchJobs')}
                  fetcher={({ search, limit, page }) =>
                    jobsApi.list({
                      search,
                      limit,
                      page,
                      client_id: clientId || undefined,
                      sort_column: 'scheduled_date',
                      sort_order: 'desc',
                    })
                  }
                  getOptionLabel={jobLabel}
                  getOptionValue={j => j.id}
                  renderOption={j => (
                    <span className='flex min-w-0 flex-col gap-0.5'>
                      <span className='truncate font-medium'>
                        {j.reference ?? j.id.slice(0, 8)}
                      </span>
                      <span className='truncate text-xs text-muted-foreground'>
                        {[
                          j.client_name,
                          j.scheduled_date
                            ? formatDate(j.scheduled_date)
                            : null,
                          j.amount_to_collect
                            ? formatCurrency(j.amount_to_collect, 'EUR', 2)
                            : null,
                        ]
                          .filter(Boolean)
                          .join(' - ')}
                      </span>
                    </span>
                  )}
                  initialLabel={selectedJob ? jobLabel(selectedJob) : undefined}
                  value={jobId || null}
                  onChange={(value, job) => {
                    setJobId(value)
                    setLinkedJob(job)
                    setClientId(job.client_id)
                    setContractId('')
                    setItems([lineItemFromJob(job)])
                    setErrors(e => ({
                      ...e,
                      client_id: undefined,
                      items: undefined,
                      itemFields: undefined,
                    }))
                    setPrefilledJobId(job.id)
                  }}
                  className={invoicePickerClass}
                />
              </div>
              <div className='flex w-full flex-col items-start gap-1'>
                <Label className={invoiceLabelClass}>
                  {t('form.contractNo')}
                </Label>
                <EntityPicker<Contract>
                  queryKey={`contracts-${clientId || 'none'}`}
                  preload={!!clientId}
                  placeholder={t('form.searchContracts')}
                  fetcher={({ search, limit, page }) =>
                    contractsApi.list({
                      search,
                      limit,
                      page,
                      client_id: clientId || undefined,
                      sort_column: 'created_at',
                      sort_order: 'desc',
                    })
                  }
                  getOptionLabel={contractLabel}
                  getOptionValue={c => c.id}
                  renderOption={c => (
                    <span className='flex min-w-0 flex-col gap-0.5'>
                      <span className='truncate font-medium'>
                        {c.number ?? c.id.slice(0, 8)}
                      </span>
                      {c.title && (
                        <span className='truncate text-xs text-muted-foreground'>
                          {c.title}
                        </span>
                      )}
                    </span>
                  )}
                  initialLabel={
                    detail?.data?.contract
                      ? contractLabel(detail.data.contract)
                      : undefined
                  }
                  value={contractId || null}
                  onChange={setContractId}
                  disabled={!clientId}
                  className={invoicePickerClass}
                />
              </div>
              <div className='flex w-full flex-col items-start gap-1'>
                <Label className={invoiceLabelClass}>
                  {t('form.paymentDeadline')}
                </Label>
                <Select
                  value={paymentDeadlineDays ? String(paymentDeadlineDays) : ''}
                  onValueChange={value => {
                    setPaymentDeadlineDays(Number(value))
                  }}
                >
                  <SelectTrigger className={invoiceSelectClass}>
                    <SelectValue
                      placeholder={t('form.paymentDeadlinePlaceholder')}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_DEADLINE_OPTIONS.map(days => (
                      <SelectItem key={days} value={String(days)}>
                        {t('form.paymentDeadlineDays', { days })}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className='flex w-full flex-col items-start gap-1'>
                <Label htmlFor='purchase-order' className={invoiceLabelClass}>
                  {t('form.purchaseOrder')}
                </Label>
                <Input
                  id='purchase-order'
                  value={purchaseOrder}
                  placeholder={t('form.enterHere')}
                  maxLength={150}
                  className={invoiceFieldClass}
                  onChange={e => setPurchaseOrder(e.target.value)}
                />
              </div>
            </div>
          </div>
        </section>

        <InvoiceStepCard step={2} title={t('form.lineItems')}>
          <LineItemsEditor
            items={items}
            onChange={nextItems => {
              setItems(nextItems)
              setErrors(current => {
                const missingUnitPrices = missingUnitPriceIndexes(nextItems)
                return {
                  ...current,
                  items: undefined,
                  itemFields:
                    current.itemFields && missingUnitPrices.length > 0
                      ? Object.fromEntries(
                          missingUnitPrices.map(index => [
                            index,
                            { unit_price: true },
                          ])
                        )
                      : undefined,
                }
              })
            }}
            error={errors.items}
            itemErrors={errors.itemFields}
            required
            hideHeading
            fractionDigits={2}
            hideTotals
            variant='quote'
            onLibraryClick={() => {
              setLibraryPageOpen(true)
              window.scrollTo({ top: 0 })
            }}
            onAddItemClick={() => setServiceDrawerOpen(true)}
            tradeDiscountRow={
              hasTradeDiscountRow
                ? {
                    title: t('form.tradeDiscountCard.rowTitle'),
                    description: t('form.tradeDiscountCard.rowDescription'),
                    amount: totals.discount_amount,
                    actionLabel: tQuotes('form.tradeDiscountCard.modify'),
                    onAction: scrollToTradeDiscountEditor,
                  }
                : null
            }
          />
        </InvoiceStepCard>

        <InvoiceStepCard
          step={3}
          title={t('form.optionalCharges')}
          contentClassName='gap-0 px-0 pb-0'
        >
          <div className='px-6 pb-4'>
            <OptionalChargesSection
              value={surcharges}
              onChange={setSurcharges}
              error={errors.surcharges}
              hideHeading
              fractionDigits={2}
              vatRate={surchargeVatRate}
              onAddChargeClick={() => {
                setEditingOptionalFeeIndex(null)
                setOptionalFeeDrawerOpen(true)
              }}
              quickPresets={optionalFeeQuickPresets}
            />
          </div>
          <QuoteGrandTotalSummary
            subtotal={totals.subtotal}
            total={totals.total}
            surchargeTotal={surchargeTotal}
            grandTotal={totals.total}
            tradeDiscountContent={
              tradeDiscountOpen ? (
                <div ref={tradeDiscountEditorRef}>
                  <TradeDiscountEditor
                    value={{ type: discountType, value: discountValue }}
                    amount={totals.discount_amount}
                    onChange={nextDiscount => {
                      setDiscountType(
                        nextDiscount.type === 'percent' ? 'percent' : 'fixed'
                      )
                      setDiscountValue(nextDiscount.value ?? 0)
                    }}
                    onRemove={() => {
                      setTradeDiscountOpen(false)
                      setDiscountValue(0)
                    }}
                  />
                </div>
              ) : undefined
            }
            tradeDiscountAmount={totals.discount_amount}
            tradeDiscountRowLabel={tQuotes('form.tradeDiscountCard.rowTitle')}
            tradeDiscountLabel={
              tradeDiscountOpen ? undefined : t('form.tradeDiscount')
            }
            onTradeDiscountClick={() => {
              setDiscountType('fixed')
              setDiscountValue(0)
              setTradeDiscountOpen(true)
            }}
            taxBreakdown={taxBreakdown}
          />
        </InvoiceStepCard>

        <div className='grid items-stretch gap-6 lg:grid-cols-2'>
          <InvoiceStepCard
            step={4}
            title={t('form.notesForClient')}
            contentClassName='min-h-[164px]'
          >
            <Textarea
              id='notes'
              value={notes}
              maxLength={2000}
              rows={5}
              className={invoiceTextareaClass}
              onChange={e => setNotes(e.target.value)}
              placeholder={t('form.notesPlaceholder')}
            />
          </InvoiceStepCard>

          {/* Attachments (feedback #44). Persisting attachments needs an invoice
              id, so on create we collect locally and flush after save. */}
          <InvoiceStepCard step={5} title={t('form.attachFiles')}>
            <PendingAttachFiles
              attachTo='invoice'
              ref={pendingAttachments}
              entityId={mode === 'edit' ? id : undefined}
              clientId={clientId || undefined}
              uploadFn={invoicesApi.uploadDocument}
              attachExistingFn={invoicesApi.attachExistingDocument}
              listExistingFn={invoicesApi.listDocuments}
              removeFn={invoicesApi.removeDocument}
              renameFn={invoicesApi.renameDocument}
              hideHeading
              brandTabs
            />
          </InvoiceStepCard>
        </div>
      </div>

      <footer className='sticky bottom-0 z-20 mt-auto -mb-6 box-border h-[72px] border-t border-[#e7e9ef] bg-white shadow-[0_-1px_4px_0_rgba(38,45,66,0.05)]'>
        <div className='flex h-full flex-col gap-4 px-16 sm:flex-row sm:items-center sm:gap-6'>
          <div className='min-w-0 flex-1'>
            <div className='text-xs font-medium leading-[1.4] text-[#6c7285]'>
              {t('form.summary.totalInclVat')}
            </div>
            <div className='font-heading text-base font-semibold leading-[1.2] tracking-[-0.48px] text-[#171a20]'>
              {formatCurrency(totals.total, 'EUR', 2)}
            </div>
          </div>
          <Button
            type='button'
            className={primaryHeaderBtnClass}
            disabled={saving}
            onClick={openSaveAndSend}
          >
            <Send className='size-4' />
            {t('form.sendToClient')}
          </Button>
        </div>
      </footer>

      <InvoiceSaveAndSendDialog
        open={saveAndSendOpen}
        busy={saving}
        onOpenChange={setSaveAndSendOpen}
        onValidateAndSend={validateAndSend}
        onSendAsDraft={sendAsDraft}
      />

      {libraryPageOpen && (
        <ServiceItemLibraryPage
          destination='invoice'
          onClose={() => {
            setLibraryPageOpen(false)
            window.scrollTo({ top: 0 })
          }}
          onPick={appendFromLibrary}
        />
      )}

      <AddQuoteServiceDrawer
        open={serviceDrawerOpen}
        onOpenChange={setServiceDrawerOpen}
        onAdd={appendManualService}
      />

      <OptionalFeeDocumentDialog
        key={`${optionalFeeDrawerOpen ? 'open' : 'closed'}:${editingOptionalFee?.id ?? editingOptionalFeeIndex ?? 'new'}`}
        open={optionalFeeDrawerOpen}
        onOpenChange={open => {
          setOptionalFeeDrawerOpen(open)
          if (!open) setEditingOptionalFeeIndex(null)
        }}
        onAdd={appendOptionalFee}
        onUpdate={updateOptionalFee}
        initialCharge={editingOptionalFee}
        defaultVatRate={surchargeVatRate}
      />
    </div>
  )
}
